import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { useOPsTableColumns } from '../hooks/useOPsTableColumns';
import { useCallback, useMemo, useState } from 'react';
import { Button, styled, useTheme } from '@mui/material';
import { TableV1 } from '../../../components/Table/TableV1';
import { SearchInput } from '../../../components/SearchInput/SearchInput';
import { ShowInactiveToggle } from '../../../components/ShowInactiveToggle/ShowInactiveToggle';
import { SelectItem, USER_COUNTRY } from '../../../types';
import { isSubmitter } from '../../../utils/roles';
import { Typography } from '../../../components/Typography/Typography';
import { useUsers } from '../../../queries/hooks/useUsers';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../../state/UIState';

const countryList = [
  { id: 1, value: USER_COUNTRY.US },
  { id: 2, value: USER_COUNTRY.UK },
];

type OPTableProps = {
  /**
   * When set, restrict the table to submitters whose workflow overrides the Submitter (first-stage)
   * DISPLAY label to exactly this value (e.g. "Operating Partner" / "Asset Servicer"). Omitted = all
   * submitters. DISPLAY-only grouping; never affects roles or authorization.
   */
  submitterLabel?: string;
};

export const OPTable = ({ submitterLabel }: OPTableProps) => {
  const [showInactive, setShowInactive] = useState(false);
  // Fetch everyone and filter here, so the toggle can show the inactive count without a second request.
  const { isLoading: areUsersLoading, data: users } = useUsers('all');
  const { colors } = useTheme();
  const columns = useOPsTableColumns();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedCountry, setSelectedCountry] = useState<SelectItem[]>([]);

  const allOPs = useMemo(() => {
    const submitters = (users ?? []).filter((user) => isSubmitter(user));
    if (!submitterLabel) return submitters;
    return submitters.filter((user) => user.workflow?.stageLabels?.['SUBMITTER'] === submitterLabel);
  }, [users, submitterLabel]);

  // Counted on this tab's submitters only, so each tab reports its own hidden rows.
  const inactiveCount = useMemo(
    () => allOPs.filter((user) => user.isActive === false).length,
    [allOPs]
  );

  const visibleOPs = useMemo(
    () =>
      allOPs
        .filter((user) => showInactive || user.isActive !== false)
        .sort((a, b) => Number(a.isActive === false) - Number(b.isActive === false)),
    [allOPs, showInactive]
  );

  const table = useReactTable({
    data: visibleOPs,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (user) => String(user.id),
  });

  const onChangeCountry = useCallback(
    (countries: SelectItem[] | undefined) => {
      if (countries) {
        setSelectedCountry(countries);
        table?.getColumn('country')?.setFilterValue(countries);
      }
    },
    [table]
  );

  const isResetDisabled = useMemo(() => {
    const filterValues = columnFilters.map((filter) => filter.value);
    if (filterValues.every((val: any) => !val || val.length === 0) && !showInactive) return true;
    return false;
  }, [columnFilters, showInactive]);

  const onFiltersReset = useCallback(() => {
    table.getColumn('name')?.setFilterValue('');
    table.getColumn('country')?.setFilterValue([]);
    setSelectedCountry([]);
    setShowInactive(false);
  }, [table, setSelectedCountry]);

  if (areUsersLoading || !users) return <TableSkeletonLoader />;

  return (
    <Wrapper>
      <FiltersSection>
        <SearchInput
          placeholder='Search'
          style={{ width: isSmallScreen ? '150px' : '370px', height: '36px' }}
          onClear={() => table.getColumn('name')?.setFilterValue('')}
          onChange={(e) => table.getColumn('name')?.setFilterValue(e.target.value)}
          value={table.getColumn('name')?.getFilterValue() ?? ''}
        />
        <Multiselect
          style={{ width: isSmallScreen ? '150px' : '200px', marginRight: '14px' }}
          fieldPlaceholder={isSmallScreen ? 'Country' : 'Select country'}
          value={selectedCountry}
          onChange={(_, countries) => onChangeCountry(countries)}
          options={countryList}
        />
        <ShowInactiveToggle
          checked={showInactive}
          onChange={setShowInactive}
          label='Show inactive'
          count={inactiveCount}
        />
        <Button
          onClick={onFiltersReset}
          variant='text'
          style={{ height: '28px', marginTop: '2px' }}
          disabled={isResetDisabled}
          sx={{
            marginLeft: 'auto',
            '.MuiButton-startIcon': { marginRight: '2px' },
            '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
          }}
        >
          <Typography variant='subtitle1' color={colors.textAccent.default}>
            Reset Filters
          </Typography>
        </Button>
      </FiltersSection>
      <TableV1
        table={table}
        height='auto'
        getRowClassName={(row) => (row.original.isActive === false ? 'muted-row' : undefined)}
        wrapperStyle={{
          maxHeight: 'calc(100vh - 288px)',
          overflowX: 'scroll',
          border: `1px solid ${colors.border.default}`,
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled('div')``;

const FiltersSection = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  gap: 12px;
`;
